import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from '@mui/material';
import type { StepProps } from '../../../types/enrollment.types';

export default function PersonalInfoStep({
  formData,
  errors,
  onChange,
}: StepProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('step.personal')}
      </Typography>

      {/* Last Name and First Name */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label={t('field.lastName')}
          value={formData.lastName}
          onChange={onChange('lastName')}
          error={!!errors.lastName}
          helperText={errors.lastName}
          inputProps={{ style: { fontSize: '1.2rem' } }}
          required
        />
        <TextField
          fullWidth
          label={t('field.firstName')}
          value={formData.firstName}
          onChange={onChange('firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName}
          inputProps={{ style: { fontSize: '1.2rem' } }}
          required
        />
      </Box>

      {/* Date of Birth */}
      <TextField
        fullWidth
        type="date"
        label={t('field.dateOfBirth')}
        value={formData.dateOfBirth}
        onChange={onChange('dateOfBirth')}
        error={!!errors.dateOfBirth}
        helperText={errors.dateOfBirth}
        InputLabelProps={{ shrink: true }}
        inputProps={{ style: { fontSize: '1.2rem' } }}
        required
      />

      {/* Gender */}
      <FormControl error={!!errors.gender} required>
        <FormLabel sx={{ fontSize: '1.1rem' }}>{t('field.gender')}</FormLabel>
        <RadioGroup
          row
          value={formData.gender}
          onChange={onChange('gender')}
        >
          <FormControlLabel
            value="Male"
            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />}
            label={t('field.gender.male')}
            sx={{ '& .MuiTypography-root': { fontSize: '1.1rem' } }}
          />
          <FormControlLabel
            value="Female"
            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />}
            label={t('field.gender.female')}
            sx={{ '& .MuiTypography-root': { fontSize: '1.1rem' } }}
          />
          <FormControlLabel
            value="Other"
            control={<Radio sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />}
            label={t('field.gender.other')}
            sx={{ '& .MuiTypography-root': { fontSize: '1.1rem' } }}
          />
        </RadioGroup>
        {errors.gender && (
          <Typography color="error" variant="caption">
            {errors.gender}
          </Typography>
        )}
      </FormControl>

      {/* HK ID Prefix (Optional) */}
      <TextField
        fullWidth
        label={t('field.hkId')}
        value={formData.hkIdPrefix}
        onChange={onChange('hkIdPrefix')}
        error={!!errors.hkIdPrefix}
        helperText={errors.hkIdPrefix || t('field.hkId.helper')}
        placeholder="A1234"
        inputProps={{
          style: { fontSize: '1.2rem', textTransform: 'uppercase' },
          maxLength: 5,
        }}
      />

      {/* Occupation (Optional) */}
      <TextField
        fullWidth
        label={t('field.occupation')}
        value={formData.occupation}
        onChange={onChange('occupation')}
        error={!!errors.occupation}
        helperText={errors.occupation}
        inputProps={{ style: { fontSize: '1.2rem' } }}
      />
    </Box>
  );
}
